import React, { useEffect } from "react";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

import Navbar from "../navbar/Navbar";
import Section from "../section/Section";
import Service from "../services/Service";
import Review from "../review/Review";
import Footer from "../footer/Footer";
import Carousel1 from "../carousel/Carousel_1";
import Gallery from "../gallery/Gallery";
import Location from "../location/Location";
import Offer from "../offer/Offer";

import "./Home.css";


export default function Home() {

  /* =====================================================
     BUSINESS DETAILS
  ===================================================== */

  const BUSINESS_NAME = "RK Sofa Works";
  const PHONE = "+919318333680";
  const WHATSAPP = "919318333680";


  /* =====================================================
     SMOOTH SCROLL
  ===================================================== */

  useEffect(() => {

    const handlePageScroll = (event) => {

      const link = event.target.closest("a[href^='#']");

      if (!link) return;

      const href = link.getAttribute("href");

      if (!href || href === "#") return;

      const sectionId = href.substring(1);

      const section = document.getElementById(sectionId);

      if (!section) return;

      event.preventDefault();

      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    };

    document.addEventListener("click", handlePageScroll);

    return () => {
      document.removeEventListener("click", handlePageScroll);
    };

  }, []);


  /* =====================================================
     FEATURE HIGHLIGHTS
  ===================================================== */

  const highlights = [
    {
      icon: "fa-solid fa-couch",
      title: "Complete Sofa Care",
      text: "Repair, restoration and finishing solutions for your existing furniture.",
    },
    {
      icon: "fa-solid fa-scissors",
      title: "Fresh Upholstery",
      text: "Give your sofa a completely refreshed look with fabric and foam replacement.",
    },
    {
      icon: "fa-solid fa-ruler-combined",
      title: "Made To Order",
      text: "Custom sofa designs created around your room, comfort and style.",
    },
    {
      icon: "fa-solid fa-house",
      title: "Doorstep Assistance",
      text: "Convenient inspection and selected sofa services at your location.",
    },
  ];


  /* =====================================================
     WORK SHOWCASE
  ===================================================== */

  const showcase = [
    {
      image: `${process.env.PUBLIC_URL}/img/sofa_repair_1.jpg`,
      number: "01",
      tag: "RESTORATION",
      title: "Classic Sofa Revival",
      description:
        "Old and tired furniture transformed with careful repair and refined finishing.",
    },
    {
      image: `${process.env.PUBLIC_URL}/img/sofa_repair_2.jpg`,
      number: "02",
      tag: "UPHOLSTERY",
      title: "New Fabric. New Feel.",
      description:
        "A fresh upholstery finish that changes the complete appearance of your sofa.",
    },
    {
      image: `${process.env.PUBLIC_URL}/img/sofa_repair_3.jpg`,
      number: "03",
      tag: "COMFORT",
      title: "Cushion Renewal",
      description:
        "Restore softness and support with suitable foam and cushion replacement.",
    },
    {
      image: `${process.env.PUBLIC_URL}/img/sofa_repair_4.jpg`,
      number: "04",
      tag: "MAKEOVER",
      title: "Complete Sofa Refresh",
      description:
        "A complete makeover for furniture that needs more than a simple repair.",
    },
  ];


  /* =====================================================
     WORK PROCESS
  ===================================================== */

  const processSteps = [
    {
      number: "01",
      title: "Tell Us Your Requirement",
      text: "Share photos, sofa details and the type of work you need.",
    },
    {
      number: "02",
      title: "Inspection & Discussion",
      text: "We understand the condition, design preference and required work.",
    },
    {
      number: "03",
      title: "Repair & Finishing",
      text: "Our craftsmen work on the sofa with attention to structure, comfort and finish.",
    },
    {
      number: "04",
      title: "Ready For Your Home",
      text: "Once the work is completed, your refreshed furniture is ready to use again.",
    },
  ];


  /* =====================================================
     SERVICE AREAS
  ===================================================== */

  const areas = [
    "Noida",
    "Greater Noida",
    "Ghaziabad",
    "Delhi",
    "Faridabad",
    "Gurugram",
    "Dwarka",
    "Rohini",
    "Saket",
    "Indirapuram",
    "Vaishali",
    "Kaushambi",
  ];


  /* =====================================================
     CUSTOMER STORIES
  ===================================================== */

  const testimonials = [
    {
      name: "Amit Verma",
      initials: "AV",
      text:
        "The sofa upholstery was done neatly and the final finish completely changed the look of our living room.",
    },
    {
      name: "Sana Khan",
      initials: "SK",
      text:
        "Our old cushions were uncomfortable. After the foam replacement, the sofa feels much better.",
    },
    {
      name: "Rohit Sharma",
      initials: "RS",
      text:
        "Professional service and good finishing. The team understood exactly what we wanted for our sofa.",
    },
  ];


  /* =====================================================
     WHATSAPP
  ===================================================== */

  const whatsappMessage = encodeURIComponent(
    "Hi, I would like to enquire about sofa repair and upholstery service from RK Sofa Works."
  );

  const whatsappURL =
    `https://wa.me/${WHATSAPP}?text=${whatsappMessage}`;


  return (

    <main
      className="rk-home"
      data-business-name={BUSINESS_NAME}
    >


      {/* =================================================
          NAVBAR
      ================================================= */}

      <Navbar />


      {/* =================================================
          HERO
      ================================================= */}

      <section className="rk-hero">
        <Carousel1 />
      </section>


      {/* =================================================
          QUICK HIGHLIGHTS
      ================================================= */}

      <section className="rk-highlights">

        <div className="container">

          <div className="rk-highlight-grid">

            {highlights.map((item) => (

              <div
                className="rk-highlight"
                key={item.title}
              >

                <div className="rk-highlight-icon">
                  <i className={item.icon}></i>
                </div>

                <div>

                  <h3>
                    {item.title}
                  </h3>

                  <p>
                    {item.text}
                  </p>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =================================================
          ABOUT / CRAFTSMANSHIP
      ================================================= */}

      <section className="rk-about">

        <div className="container">

          <div className="rk-about-layout">


            {/* Left Content */}

            <div className="rk-about-content">

              <span className="rk-eyebrow">
                THE RK SOFA WORKS APPROACH
              </span>

              <h2>
                Your Sofa Deserves
                <strong> A Second Life.</strong>
              </h2>

              <p className="rk-about-lead">
                Instead of replacing good furniture, give it
                a fresh beginning with professional repair,
                upholstery and restoration.
              </p>

              <p>
                RK Sofa Works works on sofas with a focus on
                comfort, appearance and practical finishing.
                From fabric replacement and cushion work to
                complete sofa makeovers, we help turn worn
                furniture into something you are happy to keep.
              </p>


              <div className="rk-about-points">

                <div>
                  <i className="fa-solid fa-check"></i>
                  <span>Careful workmanship</span>
                </div>

                <div>
                  <i className="fa-solid fa-check"></i>
                  <span>Fabric & foam options</span>
                </div>

                <div>
                  <i className="fa-solid fa-check"></i>
                  <span>Custom furniture solutions</span>
                </div>

                <div>
                  <i className="fa-solid fa-check"></i>
                  <span>Residential & commercial work</span>
                </div>

              </div>


              <a
                href={`tel:${PHONE}`}
                className="rk-dark-button"
              >
                <span>Discuss Your Sofa</span>
                <i className="fa-solid fa-arrow-right"></i>
              </a>

            </div>


            {/* Right Visual */}

            <div className="rk-about-visual">

              <div className="rk-about-image-main">

                <img
                  src={`${process.env.PUBLIC_URL}/img/sofa_repair_2.jpg`}
                  alt="RK Sofa Works upholstery service"
                  loading="lazy"
                />

              </div>


              <div className="rk-about-badge">

                <strong>RK</strong>

                <span>
                  SOFA
                  <br />
                  WORKS
                </span>

              </div>


              <div className="rk-about-small-image">

                <img
                  src={`${process.env.PUBLIC_URL}/img/sofa_repair_3.jpg`}
                  alt="Sofa cushion restoration"
                  loading="lazy"
                />

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          INTRO / EXISTING SECTION
      ================================================= */}

      <section className="rk-intro">
        <Section />
      </section>


      {/* =================================================
          SERVICES
      ================================================= */}

      <section
        className="rk-services"
        id="services"
      >

        <div className="rk-service-heading">

          <span className="rk-eyebrow">
            WHAT WE DO
          </span>

          <h2>
            Solutions For
            <strong> Every Sofa</strong>
          </h2>

          <p>
            Whether your furniture needs a small repair or
            a complete transformation, explore our range of
            sofa care and custom furniture services.
          </p>

        </div>

        <Service />

      </section>


      {/* =================================================
          OFFER
      ================================================= */}

      <section className="rk-offer">
        <Offer />
      </section>


      {/* =================================================
          WORK SHOWCASE
      ================================================= */}

      <section
        className="rk-showcase"
        id="work"
      >

        <div className="container">

          <div className="rk-section-intro">

            <span className="rk-eyebrow">
              OUR WORK
            </span>

            <h2>
              Furniture With
              <strong> A Fresh Identity</strong>
            </h2>

            <p>
              Explore a few examples of the kind of
              transformation we create for old,
              damaged and outdated sofas.
            </p>

          </div>


          <div className="rk-showcase-grid">

            {showcase.map((item, index) => (

              <article
                className={`rk-showcase-card rk-showcase-${index + 1}`}
                key={item.title}
              >

                <div className="rk-showcase-image">

                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                  />

                  <span className="rk-showcase-number">
                    {item.number}
                  </span>

                </div>


                <div className="rk-showcase-content">

                  <span>
                    {item.tag}
                  </span>

                  <h3>
                    {item.title}
                  </h3>

                  <p>
                    {item.description}
                  </p>

                </div>

              </article>

            ))}

          </div>

        </div>

      </section>


      {/* =================================================
          PROCESS
      ================================================= */}

      <section className="rk-process">

        <div className="container">

          <div className="rk-section-intro">

            <span className="rk-eyebrow">
              HOW IT WORKS
            </span>

            <h2>
              From Enquiry To
              <strong> Finished Sofa</strong>
            </h2>

            <p>
              A straightforward process designed to make
              your sofa repair or makeover simple.
            </p>

          </div>


          <div className="rk-process-grid">

            {processSteps.map((step) => (

              <div
                className="rk-process-card"
                key={step.number}
              >

                <span className="rk-process-number">
                  {step.number}
                </span>

                <div className="rk-process-line"></div>

                <h3>
                  {step.title}
                </h3>

                <p>
                  {step.text}
                </p>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =================================================
          SERVICE AREAS
      ================================================= */}

      <section className="rk-areas">

        <div className="container">

          <div className="rk-area-layout">


            <div className="rk-area-content">

              <span className="rk-eyebrow">
                SERVICE COVERAGE
              </span>

              <h2>
                Serving Homes
                <strong> Across Delhi NCR</strong>
              </h2>

              <p>
                Looking for sofa repair, upholstery or custom
                furniture service nearby? RK Sofa Works serves
                customers across Noida and selected Delhi NCR
                locations.
              </p>

              <a
                href={`tel:${PHONE}`}
                className="rk-outline-button"
              >
                Check Availability
                <i className="fa-solid fa-arrow-right"></i>
              </a>

            </div>


            <div className="rk-area-list">

              {areas.map((area, index) => (

                <div
                  className="rk-area-pill"
                  key={area}
                >

                  <span>
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <strong>
                    {area}
                  </strong>

                  <i className="fa-solid fa-arrow-up-right-from-square"></i>

                </div>

              ))}

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          REVIEWS
      ================================================= */}

      <section className="rk-review">

        <div className="container">

          <div className="rk-section-intro">

            <span className="rk-eyebrow">
              CUSTOMER EXPERIENCES
            </span>

            <h2>
              Real Homes.
              <strong> Real Experiences.</strong>
            </h2>

            <p>
              A few words from customers who trusted us
              with their furniture.
            </p>

          </div>


          <div className="rk-review-grid">

            {testimonials.map((review) => (

              <article
                className="rk-review-card"
                key={review.name}
              >

                <div className="rk-review-top">

                  <div className="rk-review-avatar">
                    {review.initials}
                  </div>

                  <div>

                    <h3>
                      {review.name}
                    </h3>

                    <span>
                      Customer
                    </span>

                  </div>

                  <div className="rk-review-stars">
                    ★★★★★
                  </div>

                </div>


                <p>
                  “{review.text}”
                </p>


                <div className="rk-review-mark">
                  “
                </div>

              </article>

            ))}

          </div>

        </div>

      </section>


      {/* =================================================
          GALLERY
      ================================================= */}

      <section className="rk-gallery">
        <Gallery />
      </section>


      {/* =================================================
          QUOTE CTA
      ================================================= */}

      <section className="rk-quote">

        <div className="container">

          <div className="rk-quote-box">

            <div className="rk-quote-content">

              <span>
                READY TO REFRESH YOUR FURNITURE?
              </span>

              <h2>
                Let's bring your sofa
                <strong> back to life.</strong>
              </h2>

              <p>
                Send us your sofa details and discuss
                the repair, upholstery or custom work
                you have in mind.
              </p>

            </div>


            <div className="rk-quote-actions">

              <a
                href={`tel:${PHONE}`}
                className="rk-quote-call"
              >
                <i className="fa-solid fa-phone"></i>
                Call RK Sofa Works
              </a>

              <a
                href={whatsappURL}
                target="_blank"
                rel="noopener noreferrer"
                className="rk-quote-whatsapp"
              >
                <i className="fa-brands fa-whatsapp"></i>
                WhatsApp Us
              </a>

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          GOOGLE / EXISTING REVIEWS
      ================================================= */}

      <section className="rk-google-review">
        <Review />
      </section>


      {/* =================================================
          LOCATION
      ================================================= */}

      <section className="rk-location">
        <Location />
      </section>


      {/* =================================================
          FOOTER
      ================================================= */}

      <Footer />

    </main>
  );
}